import { defineStore, storeToRefs } from 'pinia';
import { watch, ref, computed, type ComputedRef, type Ref, type WatchStopHandle } from 'vue';
import type { MessageApi, TreeOption } from 'naive-ui';
import {
  AssemblyTransformToolMode,
  EditorPanelShowDesignTreeReason,
  ViewerMode,
  type PointSelectionCandidate,
  type Viewer,
} from '@puppycad/cad-core';
import { useViewer } from '@/hooks/useViewer';
import { FeatureTreeNodeKind, useFeatureTreeStore } from '@/hooks/useFeatureTree';
import { useToolbarCommandStore } from '@/stores/toolbar-command';
import { useEditingFocusManager } from '@/hooks/useEditingFocusManager';
import { resolveAssemblyMateSubmitFailureText } from '@/core/property-manager/assembly-mate-diagnostic';
import { requestEditorDesignTreeTab } from '@/views/editor/requestEditorDesignTreeTab';

/**
 * 装配体变换命令类型。
 */
export type AssemblyTransformCommand = 'assembly-move-component' | 'assembly-rotate-component';

/**
 * 装配体参考几何体命令类型。
 */
export type AssemblyReferenceCommand =
  | 'assembly-reference-plane'
  | 'assembly-reference-axis'
  | 'assembly-reference-point';

/**
 * 装配体编辑零部件命令类型。
 */
export type AssemblyEditCommand = 'assembly-edit-component';

/**
 * 装配体配合命令类型。
 */
export type AssemblyMateCommand = 'assembly-mate';

/**
 * 装配体命令联合类型。
 */
type AssemblyCommand =
  | AssemblyTransformCommand
  | AssemblyReferenceCommand
  | AssemblyEditCommand
  | AssemblyMateCommand;

/**
 * 工具栏操作提示。
 */
interface ToolbarOperationDescriptor {
  /** 操作名称。 */
  name: string;
  /** 操作提示。 */
  hint: string;
}

/**
 * 装配体工作流参数。
 */
export interface UseEditorAssemblyWorkflowOptions {
  /** 从正式项目模型解析零件范围，不在 UI 维护特征所有权。 */
  resolveFeatureDocumentScope: (objectId: string, stableNodeKey?: string) => FeatureDocumentScope | null;
  /** 当前 Viewer 模式。 */
  viewerMode: Ref<ViewerMode> | ComputedRef<ViewerMode>;
  /** 右侧面板标签。 */
  rightPanelTab: Ref<string>;
  /** 当前激活操作命令。 */
  activeOperationCommand: Ref<string | null>;
  /** 当前绘图命令。 */
  currentDrawingCommand: Ref<string | null>;
  /** 工具栏操作提示覆盖。 */
  toolbarOperationOverride: Ref<ToolbarOperationDescriptor | null>;
  /** 消息实例。 */
  message: MessageApi;
  /** 是否处于零部件放置模式。 */
  isPlacingComponent?: Ref<boolean>;
  /** 是否存在待执行草图命令。 */
  hasPendingSketchCommand: Ref<boolean> | ComputedRef<boolean>;
  /** 是否处于草图编辑模式。 */
  isSketchEditMode: Ref<boolean> | ComputedRef<boolean>;
  /** 是否处于绘图模式。 */
  isDrawingMode: Ref<boolean> | ComputedRef<boolean>;
  /** 当前修剪模式。 */
  sketchTrimMode: Ref<unknown | null>;
  /** 是否启用草图倒角。 */
  sketchCornerEnable: Ref<boolean>;
  /** 是否处于拉伸模式。 */
  isExtrudeMode: Ref<boolean> | ComputedRef<boolean>;
  /** 旋转 UI 状态。 */
  revolveUiState: Ref<{ active: boolean }> | ComputedRef<{ active: boolean }>;
  /** 旋转切除 UI 状态。 */
  revolveCutUiState: Ref<{ active: boolean }> | ComputedRef<{ active: boolean }>;
  /** 是否处于圆角/倒角模式。 */
  isBlendMode: Ref<boolean> | ComputedRef<boolean>;
  /** 设置 Viewer 模式。 */
  setViewerMode: (mode: ViewerMode) => void;
  /** 刷新编辑器状态。 */
  updateState: () => Promise<void>;
}

/**
 * 创建编辑器装配体工作流。
 *
 * 设计说明：
 * 1. 将装配体移动/旋转与装配体参考几何体统一收口到同一个编排入口；
 * 2. 所有装配体命令共享同一套 `activeOperationCommand / toolbarCommandStore / toolbarOperationOverride` 同步策略；
 * 3. `Viewer` 未初始化时统一只给出提示，不抛异常；
 * 4. 默认 Move 的静默激活、显式 Move/Rotate 会话、装配体参考几何体确认/取消都在这里统一管理。
 */
export const useEditorAssemblyWorkflow = defineStore('editor-assembly-workflow', () => {
  const { getViewer } = useViewer();
  const toolbarCommandStore = useToolbarCommandStore();
  const defaultAssemblyMoveOperation: ToolbarOperationDescriptor = {
    name: '移动零部件',
    hint: '选择零部件后可执行移动或通过 PropertyManager 输入精确位移。',
  };
  const defaultAssemblyRotateOperation: ToolbarOperationDescriptor = {
    name: '旋转零部件',
    hint: '选择零部件后可执行旋转或通过 PropertyManager 输入精确角度。',
  };
  const assemblyReferenceOperationMap: Record<AssemblyReferenceCommand, ToolbarOperationDescriptor> = {
    'assembly-reference-plane': {
      name: '装配体基准面',
      hint: '按顺序选择装配体参考实体，确认后创建装配体基准面。',
    },
    'assembly-reference-axis': {
      name: '装配体基准轴',
      hint: '按顺序选择装配体参考实体，确认后创建装配体基准轴。',
    },
    'assembly-reference-point': {
      name: '装配体基准点',
      hint: '按顺序选择装配体参考实体，确认后创建装配体基准点。',
    },
  };
  const defaultAssemblyMateOperation: ToolbarOperationDescriptor = {
    name: '配合',
    hint: '创建装配体标准配合。',
  };
  let currentOptions: UseEditorAssemblyWorkflowOptions | null = null;
  const stopHandles: WatchStopHandle[] = [];
  const projectName = ref<string>('');
  // 编辑焦点管理器：负责格式化“编辑零部件”模式下的文档标题（零件名 ← 装配体名）
  const editingFocusManager = useEditingFocusManager();
  // 当前显示在顶部标题栏的名称（可能已被 EditingFocusManager 格式化）
  const displayedProjectTitle = ref<string>('');

  //------ 编辑零部件状态 开始------
  const featureTreeStore = useFeatureTreeStore();
  const { featureTreeData, selectedKeys, selectedObject } = storeToRefs(featureTreeStore);
  const isEnterEditingComponentStatus = ref<boolean>(false); // 是否进入编辑零部件状态
  const assemblyEditComponentDisabled = computed(() => { // “编辑零部件”按钮是否禁用
    void featureTreeData.value.length;
    const key = selectedKeys.value[0];
    if (!key) {
      return true;
    }
    const meta = featureTreeStore.featureTreeNodeMap.get(key);
    if (!meta || meta.kind !== FeatureTreeNodeKind.GlbNode) {
      return true;
    }
    if (meta.glbNodeRole !== "part") {
      return true;
    }
    return false;
  });
  //------ 编辑零部件状态 结束------

  /**
   * 在设计树中按 key 查找节点。
   *
   * @param nodes 当前设计树节点集合。
   * @param key 目标节点 key。
   * @returns 命中的树节点；未命中时返回 `null`。
   */
  function findFeatureTreeNodeByKey(nodes: readonly TreeOption[], key: string): TreeOption | null {
    for (const node of nodes) {
      if (String(node.key ?? '') === key) {
        return node;
      }
      const matchedChild = Array.isArray(node.children)
        ? findFeatureTreeNodeByKey(node.children, key)
        : null;
      if (matchedChild) {
        return matchedChild;
      }
    }
    return null;
  }

  /**
   * 收集指定树节点及其全部子节点 key。
   *
   * @param node 起始树节点。
   * @returns 节点 key 集合。
   */
  function collectFeatureTreeNodeKeys(node: TreeOption): string[] {
    const keys: string[] = [];
    const visit = (currentNode: TreeOption): void => {
      const key = String(currentNode.key ?? '');
      if (key) {
        keys.push(key);
      }
      currentNode.children?.forEach((childNode) => visit(childNode));
    };
    visit(node);
    return keys;
  }

  /**
   * 读取当前已初始化的宿主参数。
   *
   * @returns 当前宿主参数；未初始化时返回 `null`。
   */
  const getOptions = (): UseEditorAssemblyWorkflowOptions | null => currentOptions;

  /**
   * 清理内部 watcher。
   */
  const stopAllWatchers = (): void => {
    while (stopHandles.length > 0) {
      stopHandles.pop()?.();
    }
  };

  /**
   * 同步工具栏装配体命令状态。
   *
   * @param command 当前命令。
   * @param operation 工具栏提示。
   */
  const syncAssemblyToolbarState = (
    command: AssemblyCommand | null,
    operation: ToolbarOperationDescriptor | null,
  ): void => {
    const options = getOptions();
    if (!options) {
      return;
    }
    options.activeOperationCommand.value = command;
    options.toolbarOperationOverride.value = operation;
    if (command) {
      toolbarCommandStore.setActiveCommands([command]);
      return;
    }
    if (options.currentDrawingCommand.value) {
      toolbarCommandStore.setActiveCommands([options.currentDrawingCommand.value]);
      return;
    }
    toolbarCommandStore.clearActiveCommands();
  };

  /**
   * 判断命令是否属于装配体变换命令。
   *
   * @param command 命令标识。
   * @returns 是否为移动/旋转零部件命令。
   */
  const isAssemblyTransformCommand = (command: string | null): command is AssemblyTransformCommand => (
    command === 'assembly-move-component' || command === 'assembly-rotate-component'
  );

  /**
   * 判断命令是否属于装配体参考几何体命令。
   *
   * @param command 命令标识。
   * @returns 是否为装配体参考几何体命令。
   */
  const isAssemblyReferenceCommand = (command: string | null): command is AssemblyReferenceCommand => (
    command === 'assembly-reference-plane'
    || command === 'assembly-reference-axis'
    || command === 'assembly-reference-point'
  );
  
  /**
   * 判断命令是否属于编辑零部件命令。
   *
   * @param command 命令标识。
   * @returns 是否为编辑零部件命令。
   */
  const isAssemblyEditCommand = (command: string | null): command is AssemblyEditCommand => (
    command === 'assembly-edit-component'
  );

  /**
   * 判断命令是否属于装配体配合命令。
   *
   * @param command 命令标识。
   * @returns 是否为装配体配合命令。
   */
  const isAssemblyMateCommand = (command: string | null): command is AssemblyMateCommand => (
    command === 'assembly-mate'
  );

  /**
   * 判断命令是否属于装配体命令。
   *
   * @param command 命令标识。
   * @returns 是否为装配体命令。
   */
  const isAssemblyCommand = (command: string | null): command is AssemblyCommand => (
    isAssemblyTransformCommand(command)
    || isAssemblyReferenceCommand(command)
    || isAssemblyEditCommand(command)
    || isAssemblyMateCommand(command)
  );

  /**
   * 判断 Viewer 模式是否属于装配体同类模式。
   *
   * @param mode Viewer 模式。
   * @returns 是否为装配体空闲态或移动零部件命令态。
   */
  const isAssemblyFamilyViewerMode = (mode: ViewerMode): boolean => (
    mode === ViewerMode.Assembly || mode === ViewerMode.MoveComponent
  );

  /**
   * 判断移动零部件控制器是否已激活。
   *
   * @returns 控制器是否已激活。
   */
  const isMoveComponentControllerActive = (): boolean => {
    const viewer = getViewer();
    return Boolean(viewer?.moveComponentController?.isActive?.());
  };

  //------ 命令结束显示设计树 开始------
  /**
   * 请求命令结束后右侧面板回到设计树。
   *
   * @param reason 命令收口原因。
   * @param command 当前收口的装配体命令。
   */
  function requestDesignTreeAfterAssemblyCommand(
    reason: EditorPanelShowDesignTreeReason,
    command: AssemblyCommand,
  ): void {
    // 装配工作流不直接写 rightPanelTab，统一通过事件交给 Editor.vue 消费。
    requestEditorDesignTreeTab(reason, command);
  }
  //------ 命令结束显示设计树 结束------

  /**
   * 打开装配体移动/旋转命令，并同步 PropertyManager 状态。
   *
   * @param command 目标命令。
   * @returns 是否成功处理。
   */
  const openAssemblyTransformCommand = (command: AssemblyTransformCommand): boolean => {
    const options = getOptions();
    if (!options) {
      return false;
    }
    const viewer = getViewer();
    if (!viewer) {
      options.message.warning('Viewer 尚未初始化，暂时无法打开装配体变换命令。');
      return true;
    }
    const isRotateCommand = command === 'assembly-rotate-component';
    //------ 移动零部件 开始------
    /**
     * 移动零部件命令进入专用模式，旋转零部件仍停留装配体模式。
     *
     * 说明：
     * 1. `ViewerMode.MoveComponent` 只服务“移动零部件”命令；
     * 2. 旋转零部件当前继续复用 `ViewerMode.Assembly`；
     * 3. 这样可保证选择处理器分发来源固定为“明确模式”，不再依赖 Assembly 内部动态判断。
     */
    options.setViewerMode(isRotateCommand ? ViewerMode.Assembly : ViewerMode.MoveComponent);
    //------ 移动零部件 结束------
    viewer.moveComponentController?.open(
      isRotateCommand ? AssemblyTransformToolMode.Rotate : AssemblyTransformToolMode.Move,
    );
    options.rightPanelTab.value = 'property';
    syncAssemblyToolbarState(
      command,
      isRotateCommand ? defaultAssemblyRotateOperation : defaultAssemblyMoveOperation,
    );
    return true;
  };

  /**
   * 取消装配体变换命令。
   */
  const cancelAssemblyTransformCommand = (): void => {
    const options = getOptions();
    if (!options) {
      return;
    }
    const activeCommand = options.activeOperationCommand.value;
    const viewer = getViewer();
    viewer?.moveComponentController?.cancel?.();
    /**
     * 默认 Move 能力只是装配体空闲态的隐式控制器，不等同于用户打开了
     * “移动/旋转零部件”命令。切换到草图等非装配体模式时只需要释放控制器，
     * 不能把宿主模式和工具栏命令强行回写为 Assembly，否则会覆盖草图编辑会话。
     */
    if (!isAssemblyTransformCommand(activeCommand)) {
      return;
    }
    options.setViewerMode(ViewerMode.Assembly);
    syncAssemblyToolbarState(null, null);
    requestDesignTreeAfterAssemblyCommand(EditorPanelShowDesignTreeReason.CommandCanceled, activeCommand);
  };

  /**
   * 提交装配体变换命令。
   */
  const confirmAssemblyTransformCommand = async (): Promise<void> => {
    const options = getOptions();
    if (!options) {
      return;
    }
    const activeCommand = options.activeOperationCommand.value;
    const viewer = getViewer();
    await viewer?.moveComponentController?.commit?.();
    options.setViewerMode(ViewerMode.Assembly);
    syncAssemblyToolbarState(null, null);
    if (isAssemblyTransformCommand(activeCommand)) {
      requestDesignTreeAfterAssemblyCommand(EditorPanelShowDesignTreeReason.CommandConfirmed, activeCommand);
    }
  };

  /**
   * 打开装配体参考几何体命令，并同步 PropertyManager 状态。
   *
   * @param command 目标命令。
   * @returns 是否成功处理。
   */
  const openAssemblyReferenceCommand = (command: AssemblyReferenceCommand): boolean => {
    const options = getOptions();
    if (!options) {
      return false;
    }
    const viewer = getViewer();
    if (!viewer) {
      options.message.warning('Viewer 尚未初始化，暂时无法打开装配体参考几何体命令。');
      return true;
    }

    /**
     * 装配体参考几何体命令打开前，必须先释放默认 Move 或显式 Move/Rotate 控制器：
     * 1. 装配体空闲态下，Move 控制器可能处于静默激活状态；
     * 2. 若不先收口，会导致参考几何体与装配体拖拽控制同时持有输入语义；
     * 3. 这里直接在统一工作流层做兜底，不依赖宿主层额外记忆这条规则。
     */
    viewer.moveComponentController?.cancel?.();

    if (command === 'assembly-reference-plane') {
      viewer.referencePlaneController?.open();
      options.setViewerMode(ViewerMode.AssemblyReferencePlane);
    } else if (command === 'assembly-reference-axis') {
      viewer.referenceAxisController?.open();
      options.setViewerMode(ViewerMode.AssemblyReferenceAxis);
    } else {
      viewer.referencePointController?.open(null);
      viewer.referencePointController?.syncSelectionVisuals();
      options.setViewerMode(ViewerMode.AssemblyReferencePoint);
    }

    options.rightPanelTab.value = 'property';
    syncAssemblyToolbarState(command, assemblyReferenceOperationMap[command]);
    return true;
  };

  /**
   * 取消装配体参考几何体命令。
   */
  const cancelAssemblyReferenceCommand = (): void => {
    const options = getOptions();
    if (!options) {
      return;
    }
    const activeCommand = options.activeOperationCommand.value;
    if (!isAssemblyReferenceCommand(activeCommand)) {
      return;
    }

    const viewer = getViewer();
    if (activeCommand === 'assembly-reference-plane') {
      viewer?.referencePlaneController?.cancel();
    } else if (activeCommand === 'assembly-reference-axis') {
      viewer?.referenceAxisController?.cancel();
    } else {
      viewer?.referencePointController?.cancel();
      viewer?.referencePointController?.syncSelectionVisuals();
    }

    options.setViewerMode(ViewerMode.Assembly);
    syncAssemblyToolbarState(null, null);
    requestDesignTreeAfterAssemblyCommand(EditorPanelShowDesignTreeReason.CommandCanceled, activeCommand);
  };

  /**
   * 提交装配体参考几何体命令。
   */
  const confirmAssemblyReferenceCommand = async (): Promise<void> => {
    const options = getOptions();
    if (!options) {
      return;
    }
    const activeCommand = options.activeOperationCommand.value;
    if (!isAssemblyReferenceCommand(activeCommand)) {
      return;
    }

    const viewer = getViewer();
    if (!viewer) {
      options.message.warning('Viewer 尚未初始化，暂时无法提交装配体参考几何体命令。');
      return;
    }

    let result: { ok: boolean; reason?: string } | undefined;
    if (activeCommand === 'assembly-reference-plane') {
      result = viewer.referencePlaneController?.submit();
    } else if (activeCommand === 'assembly-reference-axis') {
      result = viewer.referenceAxisController?.submit();
    } else {
      result = viewer.referencePointController?.submit();
      viewer.referencePointController?.syncSelectionVisuals();
    }

    if (!result?.ok) {
      options.message.warning(result?.reason ?? '当前装配体参考几何体条件不足，无法提交');
      return;
    }

    options.setViewerMode(ViewerMode.Assembly);
    syncAssemblyToolbarState(null, null);
    await options.updateState();
    requestDesignTreeAfterAssemblyCommand(EditorPanelShowDesignTreeReason.CommandConfirmed, activeCommand);
  };

  /**
   * 打开装配体标准配合命令。
   *
   * @param command 目标命令。
   * @returns 是否成功处理。
   */
  const openAssemblyMateCommand = (command: AssemblyMateCommand): boolean => {
    const options = getOptions();
    if (!options) {
      return false;
    }
    const viewer = getViewer();
    if (!viewer) {
      options.message.warning('Viewer 尚未初始化，暂时无法打开配合命令。');
      return true;
    }

    viewer.moveComponentController?.cancel?.();
    viewer.referencePlaneController?.cancel?.();
    viewer.referenceAxisController?.cancel?.();
    viewer.referencePointController?.cancel?.();
    viewer.assemblyMateController?.open?.();
    options.setViewerMode(ViewerMode.Assembly);
    options.rightPanelTab.value = 'property';
    syncAssemblyToolbarState(command, defaultAssemblyMateOperation);
    return true;
  };

  /**
   * 从设计树打开既有 Mate 编辑命令。
   *
   * 设计说明：
   * 1. 设计树只提供稳定 `mateId`，既有 Mate 的实体、尺寸和预览恢复由 cad-core 控制器完成；
   * 2. 这里仅负责编排 Viewer 模式、右侧 PropertyManager 和工具栏命令态；
   * 3. 与新建 Mate 共用 `assembly-mate` 命令标识，避免 PropertyManager 注册表拆出第二套 UI。
   *
   * @param command 目标命令。
   * @param mateId 要编辑的 Mate ID。
   * @returns 是否成功处理。
   */
  const openAssemblyMateEditCommand = (command: AssemblyMateCommand, mateId: string): boolean => {
    const options = getOptions();
    if (!options) {
      return false;
    }
    const viewer = getViewer();
    if (!viewer) {
      options.message.warning('Viewer 尚未初始化，暂时无法编辑配合。');
      return true;
    }

    viewer.moveComponentController?.cancel?.();
    viewer.referencePlaneController?.cancel?.();
    viewer.referenceAxisController?.cancel?.();
    viewer.referencePointController?.cancel?.();
    const started = viewer.assemblyMateController?.beginEditMate?.(mateId) ?? false;
    if (!started) {
      options.message.warning('配合引用已失效，无法恢复编辑参数。');
      return true;
    }

    options.setViewerMode(ViewerMode.Assembly);
    options.rightPanelTab.value = 'property';
    syncAssemblyToolbarState(command, defaultAssemblyMateOperation);
    return true;
  };

  /**
   * 取消装配体标准配合命令。
   */
  const cancelAssemblyMateCommand = (): void => {
    const options = getOptions();
    if (!options) {
      return;
    }
    const activeCommand = options.activeOperationCommand.value;
    getViewer()?.assemblyMateController?.cancel?.();
    if (isAssemblyMateCommand(activeCommand)) {
      options.setViewerMode(ViewerMode.Assembly);
      syncAssemblyToolbarState(null, null);
      requestDesignTreeAfterAssemblyCommand(EditorPanelShowDesignTreeReason.CommandCanceled, activeCommand);
    }
    void options.updateState().catch((error) => {
      (globalThis as { reportError?: (reportedError: unknown) => void }).reportError?.(error);
    });
  };

  /**
   * 提交装配体标准配合命令。
   */
  const confirmAssemblyMateCommand = async (): Promise<void> => {
    const options = getOptions();
    if (!options) {
      return;
    }
    const controller = getViewer()?.assemblyMateController;
    const viewModel = controller?.getViewModel?.();
    const isEditingMate = Boolean(viewModel?.state.editingMateId);
    if ((viewModel?.state.candidates.length ?? -1) === 0) {
      controller?.cancel?.();
      options.setViewerMode(ViewerMode.Assembly);
      syncAssemblyToolbarState(null, null);
      await options.updateState();
      requestDesignTreeAfterAssemblyCommand(EditorPanelShowDesignTreeReason.CommandCanceled, 'assembly-mate');
      return;
    }

    const result = await controller?.submit?.();
    if (!result) {
      const latestViewModel = controller?.getViewModel?.();
      /**
       * 顶部确认按钮的失败提示必须和 Mate PropertyManager 面板共用同一套诊断展示入口。
       * web-client 不再识别或修正文案语义，只消费 cad-core 已归一化后的求解状态和错误消息。
       */
      options.message.warning(
        latestViewModel?.state
          ? resolveAssemblyMateSubmitFailureText(latestViewModel.state) ?? '当前配合条件不足，无法提交。'
          : '当前配合条件不足，无法提交。',
      );
      return;
    }
    /**
     * SOLIDWORKS Mate PropertyManager 允许在一次配合操作中连续新增多条配合。
     * 但编辑既有 Mate 是单条编辑命令，确认后应退出 PropertyManager。
     */
    if (isEditingMate) {
      options.setViewerMode(ViewerMode.Assembly);
      syncAssemblyToolbarState(null, null);
      await options.updateState();
      requestDesignTreeAfterAssemblyCommand(EditorPanelShowDesignTreeReason.CommandConfirmed, 'assembly-mate');
      return;
    }
    // 新建 Mate 提交后保持会话，等待用户继续选择下一组实体。
    syncAssemblyToolbarState('assembly-mate', defaultAssemblyMateOperation);
    await options.updateState();
  };

  /**
   * 静默激活装配体默认 Move 能力。
   */
  const ensureDefaultAssemblyMoveCapability = (): void => {
    const viewer = getViewer();
    const controller = viewer?.moveComponentController;
    if (!controller) {
      return;
    }
    const toolMode = controller.getToolMode?.();
    const isMoveMode = toolMode === AssemblyTransformToolMode.Move;
    if (controller.isActive?.() && isMoveMode) {
      return;
    }
    controller.activateDefaultMoveCapability?.();
  };

  /**
   * 判断当前是否允许自动激活默认 Move 会话。
   *
   * @returns 满足装配体空闲态时返回 true。
   */
  const canAutoActivateDefaultAssemblyMove = (): boolean => {
    const options = getOptions();
    if (!options) {
      return false;
    }
    if (options.viewerMode.value !== ViewerMode.Assembly) {
      return false;
    }
    if (options.activeOperationCommand.value !== null) {
      return false;
    }
    if (options.isPlacingComponent?.value) {
      return false;
    }
    if (options.hasPendingSketchCommand.value || options.isSketchEditMode.value || options.isDrawingMode.value) {
      return false;
    }
    if (options.sketchTrimMode.value !== null || options.sketchCornerEnable.value) {
      return false;
    }
    if (
      options.isExtrudeMode.value
      || options.revolveUiState.value.active
      || options.revolveCutUiState.value.active
    ) {
      return false;
    }
    if (options.isBlendMode.value) {
      return false;
    }
    return true;
  };

  /**
   * 注册当前宿主参数对应的 watcher。
   */
  const registerWatchers = (): void => {
    const options = getOptions();
    if (!options) {
      return;
    }

    stopHandles.push(
      watch(
        () => options.viewerMode.value,
        (nextMode, previousMode) => {
          if (
            isAssemblyFamilyViewerMode(previousMode)
            && !isAssemblyFamilyViewerMode(nextMode)
            && isMoveComponentControllerActive()
          ) {
            cancelAssemblyTransformCommand();
          }
        },
        { immediate: true },
      ),
    );

    stopHandles.push(
      watch(
        [
          () => options.viewerMode.value,
          () => options.activeOperationCommand.value,
          () => options.isPlacingComponent?.value ?? false,
          () => options.hasPendingSketchCommand.value,
          () => options.isSketchEditMode.value,
          () => options.isDrawingMode.value,
          () => options.sketchTrimMode.value,
          () => options.sketchCornerEnable.value,
          () => options.isExtrudeMode.value,
          () => options.revolveUiState.value.active,
          () => options.revolveCutUiState.value.active,
          () => options.isBlendMode.value,
        ],
        ([currentViewerMode]) => {
          if (currentViewerMode !== ViewerMode.Assembly || !getViewer()) {
            return;
          }
          if (!canAutoActivateDefaultAssemblyMove()) {
            return;
          }
          ensureDefaultAssemblyMoveCapability();
        },
        { immediate: true },
      ),
    );
  };

  /**
   * 在 Viewer ready 且宿主模式已同步后，补一次默认装配体 Move 能力收口。
   */
  const handleViewerReady = (): void => {
    if (!getViewer()) {
      return;
    }
    if (!canAutoActivateDefaultAssemblyMove()) {
      return;
    }
    ensureDefaultAssemblyMoveCapability();
  };

  /**
   * 初始化装配体工作流。
   *
   * @param options 当前宿主参数。
   */
  const initialize = (options: UseEditorAssemblyWorkflowOptions): void => {
    stopAllWatchers();
    currentOptions = options;
    registerWatchers();
  };

  /**
   * 销毁装配体工作流。
   */
  const dispose = (): void => {
    stopAllWatchers();
    currentOptions = null;
  };

  /**
   * 在执行非装配体命令前清理装配体会话。
   *
   * @param commandType 即将执行的命令。
   */
  const handleBeforeNonAssemblyCommand = (commandType: string): void => {
    const options = getOptions();
    if (!options) {
      return;
    }
    if (isAssemblyCommand(commandType)) {
      return;
    }
    if (isAssemblyTransformCommand(options.activeOperationCommand.value)) {
      cancelAssemblyTransformCommand();
      return;
    }
    if (isAssemblyReferenceCommand(options.activeOperationCommand.value)) {
      cancelAssemblyReferenceCommand();
      return;
    }
    if (isAssemblyMateCommand(options.activeOperationCommand.value)) {
      cancelAssemblyMateCommand();
      return;
    }
    if (options.activeOperationCommand.value === null && isMoveComponentControllerActive()) {
      getViewer()?.moveComponentController?.cancel?.();
    }
  };

  /**
   * 打开编辑零部件命令，并同步 PropertyManager 状态。
   *
   * @param command 目标命令。
   * @returns 是否成功处理。
   */
  const openAssemblyEditCommand = (command: AssemblyEditCommand): boolean => {
    // 同步 PropertyManager 状态
    const options = getOptions();
    if (!options) {
      return false;
    }
    const viewer = getViewer();
    if (!viewer) {
      options.message.warning('Viewer 尚未初始化，暂时无法打开编辑零部件命令。');
      return false;
    }
    // ------ 选中的零部件的外观为实体，其余零部件的外观为透明 开始 ------ 
    if (isEnterEditingComponentStatus.value === false) {
      const key = selectedKeys.value[0];
      const meta = key ? featureTreeStore.featureTreeNodeMap.get(key) : null;
      if (!meta || meta.kind !== FeatureTreeNodeKind.GlbNode || meta.glbNodeRole !== 'part' || !meta.objectId) {
        options.message.error('选中的零部件为空，无法进入编辑零部件模式');
        return false;
      }
      const selectedTreeNode = findFeatureTreeNodeByKey(featureTreeData.value, key);
      const documentScope = options.resolveFeatureDocumentScope(meta.objectId, meta.assemblyNodeId || meta.glbAssemblyNodeId);
      if (!documentScope) {
        options.message.error('无法解析当前零件的稳定几何范围，请重新加载项目后重试。');
        return false;
      }
      featureTreeStore.setAssemblyEditHighlightedTreeKeys(
        selectedTreeNode ? collectFeatureTreeNodeKeys(selectedTreeNode) : [key],
      );
      // 进入编辑零部件后，设计树不再保留原生选中态，只保留蓝色名称标记编辑范围。
      selectedKeys.value = [];
      selectedObject.value = null;
      // 先清理 Viewer 主选状态，避免编辑控制器把“选中态蓝色材质”缓存为原始材质。
      viewer.clearSelectionProgrammatically?.();
      isEnterEditingComponentStatus.value = true;
      viewer.enterAssemblyComponentEditMode(meta.objectId, documentScope);

      // 进入编辑零部件成功后，立即通过 EditingFocusManager 更新标题（零件名 ← 装配体名）
      const componentDisplayName =
        meta.displayName || (selectedTreeNode as any)?.label || '未命名零部件';
      editingFocusManager.enterEditComponent(componentDisplayName);
      displayedProjectTitle.value = editingFocusManager.getDocumentTitle();
    } else {
      isEnterEditingComponentStatus.value = false;
      viewer.exitAssemblyComponentEditMode();
      selectedKeys.value = [];
      selectedObject.value = null;
      featureTreeStore.clearAssemblyEditHighlightedTreeKeys();

      // 退出编辑零部件后恢复原始装配体名称
      displayedProjectTitle.value = editingFocusManager.getCurrentAssemblyName() || '未命名项目';
    }
    // ------ 选中的零部件的外观为实体，其余零部件的外观为透明 结束 ------ 
    return true;

  }

  return {
    initialize,
    dispose,
    handleViewerReady,
    handleBeforeNonAssemblyCommand,
    isAssemblyTransformCommand,
    openAssemblyTransformCommand,
    cancelAssemblyTransformCommand,
    confirmAssemblyTransformCommand,
    isAssemblyReferenceCommand,
    openAssemblyReferenceCommand,
    cancelAssemblyReferenceCommand,
    confirmAssemblyReferenceCommand,
    isAssemblyMateCommand,
    openAssemblyMateCommand,
    openAssemblyMateEditCommand,
    cancelAssemblyMateCommand,
    confirmAssemblyMateCommand,
    isAssemblyEditCommand,
    openAssemblyEditCommand,
    isEnterEditingComponentStatus,
    assemblyEditComponentDisabled,
    displayedProjectTitle
  };
});
